import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ApiDefaults, ApiService, GraphQLResponse } from '@lsq-frontend/angular-shared/api-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { BusinessUnitInput, BusinessUnitResponse, SortDirection } from '../../model/models';
import { EnumType, jsonToGraphQLQuery } from 'json-to-graphql-query';
import { FeatureFlagService } from '@lsq-frontend/common-ui';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import { startWith } from 'rxjs/operators';
import {
    ListBusinessUnitSummariesRequest,
    ListResponseBusinessUnitSummaryDto
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer-account/account-summary/list-business-unit-summaries';
import { isNil, omitBy } from 'lodash';
import { mapBUSortColumn } from '../../helpers/status-mapping';

export class BusinessUnitSummaryDto {
    amountDue?: number;
    buyerId?: string;
    buyerKey?: string;
    buyerName?: string;
    enrolledSuppliersCount?: number;
    isEnrolledInTotalPaymentManagement?: boolean;
    openInvoiceCount?: number;
    openInvoiceTotal?: number;
    percentUsed?: number;
    totalBalance?: number;
    totalEarlyPaid?: number;

    public constructor(init?: Partial<BusinessUnitSummaryDto>) {
        Object.keys(init ?? {}).forEach((k) => (this[k] = init[k]));
    }
}

@Injectable({ providedIn: 'root' })
export class ListBusinessUnitSummariesService extends ApiService<
    ListBusinessUnitSummariesRequest,
    ListResponseBusinessUnitSummaryDto
> {
    public requestType = ListBusinessUnitSummariesRequest;
    public responseType = ListResponseBusinessUnitSummaryDto;
    public endpointName = 'BuyerAccount/AccountSummary/ListBusinessUnitSummaries';
    public serviceName = 'financing';

    private _isPayLaterEnabled = false;

    constructor(
        http: HttpClient,
        @Inject(SETTINGS) settings: ISettings,
        private _featureFlagService: FeatureFlagService
    ) {
        super(http, settings);

        this._featureFlagService
            .isOn(FeatureFlags.PayLater)
            .pipe(startWith(this._featureFlagService.isOnSync(FeatureFlags.PayLater)))
            .subscribe((isOn) => {
                this._isPayLaterEnabled = isOn;
                if (this._isPayLaterEnabled) {
                    this.endpointName = 'v1/query';
                    this.serviceName = 'financingGql';
                    this.usePascalForUrl = false;
                } else {
                    this.endpointName = 'BuyerAccount/AccountSummary/ListBusinessUnitSummaries';
                    this.serviceName = 'financing';
                    this.usePascalForUrl = true;
                }
            });
    }

    public requestMapper(
        req: ListBusinessUnitSummariesRequest
    ): { query: string } | ListBusinessUnitSummariesRequest {
        if (this._isPayLaterEnabled) {
            const {
                skip,
                take,
                sortColumn,
                sortDirection,
                secondarySortColumn,
                secondarySortDirection,
                tertiarySortColumn,
                tertiarySortDirection
            } = req.pagination;

            const input: BusinessUnitInput = omitBy(
                {
                    companyFilter: {},
                    buyerKeys: req.filter.buyerKeys,
                    businessUnitNameFilter: req.filter.searchField || '',
                    pagination: {
                        skip: skip ?? 0,
                        take: take ?? ApiDefaults.TakeAll,
                        sortColumn: new EnumType(mapBUSortColumn(sortColumn)),
                        sortDirection: new EnumType(
                            SortDirection[sortDirection] ?? SortDirection.ASCENDING
                        ),
                        secondarySortColumn: new EnumType(mapBUSortColumn(secondarySortColumn)),
                        secondarySortDirection: new EnumType(
                            SortDirection[secondarySortDirection] ?? SortDirection.ASCENDING
                        ),
                        tertiarySortColumn: new EnumType(mapBUSortColumn(tertiarySortColumn)),
                        tertiarySortDirection: new EnumType(
                            SortDirection[tertiarySortDirection] ?? SortDirection.ASCENDING
                        )
                    }
                },
                isNil
            );

            const query = {
                businessUnits: {
                    __args: { input },
                    rows: {
                        buyerId: true,
                        buyerKey: true,
                        buyerCompanyName: true,
                        supplierCount: true,
                        openInvoiceCount: true,
                        openInvoiceAmount: {
                            cents: true,
                            currencyCode: true
                        },
                        earlyPaidAmount: {
                            cents: true,
                            currencyCode: true
                        },
                        usedPercentage: true,
                        amountDue: {
                            cents: true,
                            currencyCode: true
                        },
                        totalBalanceAmount: {
                            cents: true,
                            currencyCode: true
                        },
                        scfConfig: {
                            isFboManageAllPayments: true
                        }
                    },
                    totals: {
                        totalCount: true
                    }
                }
            };
            return { query: `{${jsonToGraphQLQuery(query)}}` };
        }
        return req;
    }

    public responseMapper(
        res: GraphQLResponse<BusinessUnitResponse> | ListResponseBusinessUnitSummaryDto
    ): ListResponseBusinessUnitSummaryDto {
        if (this._isPayLaterEnabled) {
            const { rows, totals } = (res as GraphQLResponse<
                BusinessUnitResponse
            >).data.businessUnits;

            return new ListResponseBusinessUnitSummaryDto({
                ok: true,
                totalCount: totals.totalCount,
                rows: rows.map((businessUnit) => {
                    return new BusinessUnitSummaryDto({
                        amountDue: businessUnit.amountDue.cents / 100,
                        buyerId: businessUnit.buyerId,
                        buyerKey: businessUnit.buyerKey,
                        buyerName: businessUnit.buyerCompanyName,
                        enrolledSuppliersCount: businessUnit.supplierCount,
                        isEnrolledInTotalPaymentManagement:
                            businessUnit.scfConfig.isFboManageAllPayments,
                        openInvoiceCount: businessUnit.openInvoiceCount,
                        openInvoiceTotal: businessUnit.openInvoiceAmount.cents / 100,
                        percentUsed: businessUnit.usedPercentage,
                        totalBalance: businessUnit.totalBalanceAmount.cents / 100,
                        totalEarlyPaid: businessUnit.earlyPaidAmount.cents / 100
                    });
                })
            });
        }
        return res as ListResponseBusinessUnitSummaryDto;
    }

    public get endpointUrl(): string {
        return this._isPayLaterEnabled ? super.endpointUrl.replace('/api', '') : super.endpointUrl;
    }
}
